// src/components/institution/manager/GalleriesTab.jsx
import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter,
  Image,
  Eye,
  Grid,
  Upload
} from 'lucide-react';
import { institutionService } from '../../../services/institutionService';
import { GalleryForm } from '../forms/GalleryForm';

export const GalleriesTab = ({ bookId, institutionData, onUpdate }) => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingGallery, setEditingGallery] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    loadGalleries();
  }, [bookId]);

  const loadGalleries = async () => {
    try {
      const data = await institutionService.getGalleries(bookId);
      setGalleries(data);
    } catch (error) {
      console.error('Error loading galleries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (galleryData) => {
    try {
      if (editingGallery) {
        await institutionService.updateGallery(bookId, editingGallery.id, galleryData);
      } else {
        await institutionService.createGallery(bookId, galleryData);
      }
      await loadGalleries();
      await onUpdate();
      setShowForm(false);
      setEditingGallery(null);
    } catch (error) {
      console.error('Error saving gallery:', error);
      alert('Failed to save gallery');
    }
  };

  const handleDelete = async (galleryId) => {
    if (!window.confirm('Are you sure you want to delete this gallery?')) return;
    
    try {
      await institutionService.deleteGallery(bookId, galleryId);
      await loadGalleries();
      await onUpdate();
    } catch (error) {
      console.error('Error deleting gallery:', error);
      alert('Failed to delete gallery');
    }
  };

  const filteredGalleries = galleries.filter(gallery => {
    const matchesSearch = gallery.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         gallery.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || gallery.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Photo Galleries</h2>
          <p className="text-gray-600">Manage institution photo galleries and media</p>
        </div>
        <button
          onClick={() => {
            setEditingGallery(null);
            setShowForm(true);
          }}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Create Gallery
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Search galleries..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-full"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="all">All Categories</option>
            <option value="campus">Campus</option>
            <option value="events">Events</option>
            <option value="facilities">Facilities</option>
            <option value="academic">Academic</option>
            <option value="sports">Sports</option>
          </select>
        </div>
      </div>

      {/* Galleries Grid */}
      {filteredGalleries.length === 0 ? (
        <div className="text-center py-12">
          <Image className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Galleries Found</h3>
          <p className="text-gray-600">Create your first photo gallery to showcase your institution.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGalleries.map((gallery) => (
            <div key={gallery.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="aspect-w-16 aspect-h-9 bg-gray-200">
                {gallery.coverImage ? (
                  <img
                    src={gallery.coverImage}
                    alt={gallery.title}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-48">
                    <Image className="h-12 w-12 text-gray-400" />
                  </div>
                )}
              </div>
              
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">
                    {gallery.title}
                  </h3>
                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full ml-2">
                    {gallery.category}
                  </span>
                </div>
                
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {gallery.description}
                </p>
                
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <div className="flex items-center gap-1">
                    <Grid className="h-4 w-4" />
                    {gallery.imageCount || 0} photos
                  </div>
                  <div className="flex items-center gap-1">
                    <Eye className="h-4 w-4" />
                    {gallery.views || 0} views
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setEditingGallery(gallery);
                      setShowForm(true);
                    }}
                    className="flex-1 bg-indigo-600 text-white py-2 px-3 rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(gallery.id)}
                    className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Gallery Form Modal */}
      {showForm && (
        <GalleryForm
          gallery={editingGallery}
          onSave={handleSave}
          onClose={() => {
            setShowForm(false);
            setEditingGallery(null);
          }}
          bookId={bookId}
        />
      )}
    </div>
  );
};